import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { mockLaunches } from '../services/dataService';
import ProtectedRoute from '../components/ProtectedRoute';

/**
 * Chat Page Component
 * Premium subscribers only
 */
const Chat = () => {
  const { roomId } = useParams();
  const { user } = useAuth();
  const { messages, activeRoom, getRoomMessages, sendMessage, joinRoom, leaveRoom, canAccessChat } = useChat();
  const { canAccessPremium } = useSubscription();
  const [messageText, setMessageText] = useState('');
  const [selectedRoom, setSelectedRoom] = useState(roomId || null);
  const messagesEndRef = useRef(null);

  // Available chat rooms
  const availableRooms = [
    { id: 'general', name: 'General Discussion', type: 'general' },
    { id: 'upcoming-launches', name: 'Upcoming Launches', type: 'group' },
    ...mockLaunches
      .filter(l => new Date(l.launchDate) > new Date())
      .map(l => ({ id: `launch-${l.id}`, name: l.missionName, type: 'launch', launchId: l.id }))
  ];

  useEffect(() => {
    if (selectedRoom) {
      joinRoom(selectedRoom);
    }
    return () => {
      leaveRoom();
    };
  }, [selectedRoom, joinRoom, leaveRoom]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, activeRoom]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!messageText.trim() || !selectedRoom) return;

    sendMessage(selectedRoom, messageText);
    setMessageText('');
  };

  if (!canAccessChat || !canAccessPremium()) {
    return (
      <div className="container" style={{ paddingTop: '50px', textAlign: 'center' }}>
        <div className="card">
          <h2 style={{ marginBottom: '20px' }}>Premium Feature</h2>
          <p style={{ marginBottom: '20px', color: 'var(--text-dark)' }}>
            You need a Premium subscription to access chat rooms.
          </p>
          <Link to="/subscription" className="btn btn-primary">
            Subscribe Now
          </Link>
        </div>
      </div>
    );
  }

  const roomMessages = selectedRoom ? getRoomMessages(selectedRoom) : [];

  return (
    <ProtectedRoute>
      <div className="container" style={{ paddingTop: '30px', maxWidth: '1200px' }}>
        <h1 style={{ fontSize: '36px', marginBottom: '30px', textAlign: 'center' }}>
          💬 Chat Rooms
        </h1>

        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '20px' }}>
          {/* Sidebar - Room List */}
          <div className="card" style={{ height: 'fit-content' }}>
            <h2 style={{ fontSize: '20px', marginBottom: '20px', color: 'var(--highlight-color)' }}>
              Available Rooms
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {availableRooms.map(room => (
                <button
                  key={room.id}
                  onClick={() => setSelectedRoom(room.id)}
                  className={selectedRoom === room.id ? 'btn btn-primary' : 'btn btn-secondary'}
                  style={{ textAlign: 'left', width: '100%' }}
                >
                  {room.type === 'launch' && '🚀 '}
                  {room.type === 'group' && '👥 '}
                  {room.type === 'general' && '💬 '}
                  {room.name}
                </button>
              ))}
            </div>
          </div>

          {/* Main Chat Area */}
          <div className="card" style={{ display: 'flex', flexDirection: 'column', height: '600px' }}>
            {selectedRoom ? (
              <>
                <div style={{ 
                  marginBottom: '20px', 
                  paddingBottom: '15px',
                  borderBottom: '2px solid var(--card-hover)'
                }}>
                  <h2 style={{ fontSize: '24px', color: 'var(--highlight-color)' }}>
                    {availableRooms.find(r => r.id === selectedRoom)?.name || 'Chat Room'}
                  </h2>
                </div>

                {/* Messages */}
                <div style={{ 
                  flex: 1, 
                  overflowY: 'auto', 
                  marginBottom: '20px',
                  padding: '10px',
                  background: 'var(--primary-color)',
                  borderRadius: '5px'
                }}>
                  {roomMessages.length > 0 ? (
                    roomMessages.map(msg => (
                      <div 
                        key={msg.id} 
                        style={{ 
                          marginBottom: '15px',
                          padding: '10px',
                          background: msg.userId === user?.id ? 'var(--accent-color)' : 'var(--card-hover)',
                          borderRadius: '5px',
                          marginLeft: msg.userId === user?.id ? 'auto' : '0',
                          maxWidth: '70%',
                          marginRight: msg.userId === user?.id ? '0' : 'auto'
                        }}
                      >
                        <div style={{ 
                          fontSize: '12px', 
                          color: 'var(--text-dark)', 
                          marginBottom: '5px',
                          fontWeight: 'bold'
                        }}>
                          {msg.userName}
                        </div>
                        <div>{msg.text}</div>
                        <div style={{ 
                          fontSize: '10px', 
                          color: 'var(--text-dark)', 
                          marginTop: '5px'
                        }}>
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ textAlign: 'center', color: 'var(--text-dark)', padding: '20px' }}>
                      No messages yet. Start the conversation!
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Type a message..."
                    style={{ flex: 1, padding: '10px', borderRadius: '5px', border: '1px solid var(--accent-color)', background: 'var(--card-bg)', color: 'var(--text-light)' }}
                  />
                  <button type="submit" className="btn btn-primary">
                    Send
                  </button>
                </form>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '50px', color: 'var(--text-dark)' }}>
                <h3 style={{ marginBottom: '20px' }}>Select a chat room to start chatting</h3>
                <p>Choose a room from the sidebar to join the conversation.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
};

export default Chat;
